import React, { useEffect, useMemo, useRef, useState, useCallback } from "react"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import {
  Button,
  SecondaryHeader,
  SecondaryHeaderButtonWrap,
  SecondaryHeaderInfo,
  SecondaryHeaderSearchField,
  Spinner,
} from "olive-react-myoneui"
import PaginationTemplate from "../component/grid-table/PaginationTemplate"
import ConfirmBox from "../component/ConfirmBox"
import AddOffcanvas from "./AddOffcanvas"
import EditOffcanvas from "./EditOffcanvas"
import DataNotFound from "../component/DataNotFound"
import { getQuestion, deleteQuestion } from "../../../api/questionApis"
import { debounce } from "../../../utils/helper"
import { useNavigate } from "react-router-dom"
import "./QuestionsCard.css"
import PdfReader from "../../../components/PdfReader"
import { truncate } from "../../../utils/truncate"

const placeholderImg =
  "https://shorthand.com/the-craft/raster-images/assets/5kVrMqC0wp/sh-unsplash_5qt09yibrok-4096x2731.jpeg"

function getErrorMessage(error) {
  if (!error) return "Something went wrong"
  if (error.response && error.response.data) {
    const data = error.response.data
    if (data.message) return data.message
    if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors[0].msg || data.errors[0]
    }
  }
  return error.message || String(error)
}

export default function Questions() {
  const navigate = useNavigate()
  const [showConfirmBox, setShowConfirmBox] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [loading, setLoading] = useState(false)
  const [operationLoading, setOperationLoading] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [questionId, setQuestionId] = useState(null)
  const [pagination, setPagination] = useState({ page: 1, pageSize: 12 })
  const [totalItems, setTotalItems] = useState(0)
  const [totalPage, setTotalPage] = useState(1)
  const [data, setData] = useState([])
  const [search, setSearch] = useState("")
  const [searchText, setSearchText] = useState("")
  const apiCallInProgress = useRef(false)

  const debouncedSetSearchText = useMemo(
    () =>
      debounce((val) => {
        setPagination((prev) => ({ ...prev, page: 1 }))
        setSearchText(val)
      }, 500),
    [],
  )

  const onSearchChange = (e) => {
    const val = e.target.value
    setSearch(val)
    debouncedSetSearchText(val)
  }

  const onClearSearch = () => {
    setSearch("")
    setSearchText("")
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const fetchQuestions = useCallback(async () => {
    if (apiCallInProgress.current) return
    apiCallInProgress.current = true
    setLoading(true)
    try {
      const params = {
        page: pagination.page,
        limit: pagination.pageSize,
        ...(searchText ? { title: searchText } : {}),
      }

      const response = await getQuestion(params)

      if (response && response.data) {
        const list = response.data.data || []
        const totalItems = response.data?.totalItems || 0
        const totalPage = pagination.pageSize
          ? Math.ceil(totalItems / pagination.pageSize)
          : 0
        setData(list)
        setTotalItems(response.data?.totalItems || 0)
        setTotalPage(totalPage)
      }
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setLoading(false)
      apiCallInProgress.current = false
    }
  }, [pagination.page, pagination.pageSize, searchText])

  useEffect(() => {
    fetchQuestions()
  }, [fetchQuestions])
  useEffect(() => {
    const totalPages = Math.ceil(totalItems / pagination.pageSize)
    if (pagination.page > totalPages && totalPages > 0) {
      setPagination((prev) => ({ ...prev, page: totalPages }))
    }
  }, [totalItems, pagination.pageSize])

  const totalPages = Math.ceil(totalItems / pagination.pageSize)

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return
    setPagination((prev) => ({ ...prev, page: newPage }))
  }

  const handlePageSizeChange = (newSize) => {
    if (!loading && !operationLoading && newSize !== pagination.pageSize) {
      setPagination({ page: 1, pageSize: newSize })
    }
  }

  const handleDeleteQuestion = (item) => {
    if (!operationLoading) {
      setShowConfirmBox(true)
      if (item) setQuestionId(item._id)
    }
  }

  const handleHideConfirmBox = () => setShowConfirmBox(false)

  const handleEditQuestion = (item) => {
    if (!operationLoading) {
      setShowEdit(true)
      setSelectedQuestion(item)
    }
  }

  const handleViewQuestion = (item) => {
    if (!item || !item._id) return toast.error("Question ID missing")
    navigate(`/admin/questions/${item._id}`)
  }

  const handleClose = () => {
    if (!operationLoading) {
      setShowAdd(false)
      setShowEdit(false)
      setSelectedQuestion(null)
      setQuestionId(null)
    }
  }

  const deleteQuestionById = async () => {
    setOperationLoading(true)
    try {
      const response = await deleteQuestion(questionId)
      if (response && response.data) {
        toast.success("Question deleted successfully")
        setShowConfirmBox(false)
        setQuestionId(null)
        await fetchQuestions()
      }
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setOperationLoading(false)
    }
  }

  const handleAddQuestionSuccess = async () => {
    setShowAdd(false)
    await fetchQuestions()
  }

  const handleUpdateQuestionSuccess = async () => {
    setShowEdit(false)
    await fetchQuestions()
  }

  return (
    <>
      <ToastContainer position='top-right' autoClose={3000} />
      <div className='content-wrapper'>
        <SecondaryHeader>
          <SecondaryHeaderInfo
            SecondaryHeaderTitle='Questions'
            SecondaryHeaderText={`${totalItems} Questions`}
            SecondaryText
          />
          <SecondaryHeaderSearchField
            placeholder='Search Questions...'
            value={search}
            onChange={onSearchChange}
            onClear={onClearSearch}
            disabled={loading || operationLoading}
          />
          <SecondaryHeaderButtonWrap>
            <Button
              variant='secondary'
              onClick={() => setShowAdd(true)}
              disabled={loading || operationLoading}
            >
              Add
            </Button>
          </SecondaryHeaderButtonWrap>
        </SecondaryHeader>

        <div className='content-block question-pool-content'>
          {loading ? (
            <div className='spinner-wrapper'>
              <Spinner animation='border' />
            </div>
          ) : data.length === 0 ? (
            <DataNotFound />
          ) : (
            <>
              <div className='card-grid'>
                {data.map((item) => (
                  <div className='question-card' key={item._id}>
                    <div className='card-img'>
                      {item?.question_file ? (
                        item.question_file
                          .split("?")[0]
                          .toLowerCase()
                          .endsWith(".pdf") ? (
                          <>
                            <PdfReader
                              file={item.question_file}
                              showFullScreenButton={true}
                              style={{
                                borderBottom: "none",
                                boxShadow: "none",
                              }}
                            />
                          </>
                        ) : (
                          <img
                            src={item.question_file || placeholderImg}
                            alt={item.title || `Question ${item._id}`}
                            onError={(e) => {
                              e.target.onerror = null
                              e.target.src = placeholderImg
                            }}
                          />
                        )
                      ) : (
                        <img src={placeholderImg} alt='placeholder' />
                      )}
                    </div>
                    <div className='card-content'>
                      <p>
                        <strong>Paper:</strong> {item.paper || "N/A"}
                      </p>
                      <p>
                        <strong>Year:</strong> {item.year || "N/A"}
                      </p>
                      <p>
                        <strong>Question_Number:</strong>{" "}
                        {item.question_number || "N/A"}
                      </p>
                      <p>
                        <strong>Subject:</strong>{" "}
                        {truncate(item?.subject?.title || "N/A", 30)}
                      </p>
                      <p>
                        <strong>Topic:</strong>{" "}
                        {truncate(item?.topics[0]?.title || "N/A", 30)}
                      </p>
                      <p>
                        <strong>SubTopic:</strong>{" "}
                        {truncate(item.subtopics[0]?.title || "N/A", 30)}
                      </p>
                      <p>
                        <strong>Chapter:</strong>{" "}
                        {truncate(
                          Array.isArray(item?.chapter) &&
                            item.chapter.length > 0
                            ? item.chapter.map((c) => c?.title || c).join(", ")
                            : item.chapter?.title || "N/A",
                          30,
                        )}
                      </p>
                      <p>
                        <strong>Section:</strong>{" "}
                        {Array.isArray(item?.section) && item.section.length > 0
                          ? item.section.map((s) => s?.title || s).join(", ")
                          : item.section?.title || "N/A"}
                      </p>

                      <div className='card-buttons'>
                        <Button
                          variant='primary'
                          size='sm'
                          onClick={() => handleViewQuestion(item)}
                        >
                          View
                        </Button>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => handleEditQuestion(item)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant='danger'
                          size='sm'
                          onClick={() => handleDeleteQuestion(item)}
                          disabled={operationLoading}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className='pagination-wrapper'>
                <PaginationTemplate
                  page={pagination.page}
                  pageSize={pagination.pageSize}
                  totalItems={totalItems}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  disabled={loading || operationLoading}
                  pageSizeOptions={[12, 24, 52, 100]}
                  disableNext={pagination.page === totalPages}
                  disablePrev={pagination.page === 1}
                  totalPages={totalPage}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmBox
        show={showConfirmBox}
        question='Are you sure you want to delete this question?'
        okText='Delete'
        cancelText='Cancel'
        onHide={handleHideConfirmBox}
        onSubmit={deleteQuestionById}
        loading={operationLoading}
      />

      <AddOffcanvas
        show={showAdd}
        onClose={handleClose}
        onAddSuccess={handleAddQuestionSuccess}
        loading={operationLoading}
      />

      <EditOffcanvas
        show={showEdit}
        onClose={handleClose}
        onSuccess={handleUpdateQuestionSuccess}
        initialData={selectedQuestion}
        loading={operationLoading}
      />
    </>
  )
}
